var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
import 'babel-polyfill';
// Import all the third party stuff
import { useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import FontFaceObserver from 'fontfaceobserver';
import 'sanitize.css/sanitize.css';
import 'react-loading-skeleton/dist/skeleton.css';
// Import root app
import Root from '@project/containers/Root';
// Import Theme Providers
import { ThemeProvider, getTokens, } from '@project/components/story-book-components/ThemeProvider';
// Import Language Provider
import LanguageProvider from '@project/containers/LanguageProvider';
import ErrorReloaderBoundary from '@project/components/ErrorReloaderBoundary';
// Load the favicon and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions */
import '!file-loader?name=[name].[ext]!./images/favicons/favicon.ico';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-32.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-128.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-152.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-167.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-180.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-192.png';
import '!file-loader?name=[name].[ext]!./images/favicons/favicon-196.png';
import 'file-loader?name=[name].[ext]!./.htaccess';
/* eslint-enable import/no-unresolved, import/extensions */
import configureStore from '@project/store';
import storageService from '@project/utils/storageService';
// Import i18n messages
import { translationMessages } from './i18n';
// Import CSS reset and Global Styles
import { GlobalStyle } from './global-styles';
import { TailwindStyle } from './tailwind-styles';
import { history } from '@project/store/reducers';
// Service worker
import { canReceiveServiceWorker, getRegistrationsServiceWorker, isPresentServiceWorker, } from './service-worker/utils';
import { getLatestPlatformVersion } from './service-worker/service';
import { PageSkeletonAppSidebar, PageSkeletonHeader, PageSkeletonWrapper, PageSkeletonSubHeader, PageSkeletonBox, } from '@project/components/base';
import { isElectron } from '@project/utils/electron';
import { AppStatusBarContainer, AppStatusBarTitle, } from '@project/containers/Root/styles';
import { isStringMajorMinorEqual } from '@project/utils/version';
// Observe loading of Open Sans (to remove open sans, remove the <link> tag in
// the index.html file and this observer)
var openSansObserver = new FontFaceObserver('Open Sans', {});
// When Open Sans is loaded, add a font-family using Open Sans to the body
openSansObserver.load().then(function () {
    document.body.classList.add('fontLoaded');
});
// Create redux store with history
var initialState = {};
var MOUNT_NODE_SKELETON = document.getElementById('app-skeleton');
var hasToken = function () { return !!localStorage.getItem('token'); };
var sidebarRoutes = ['/apps/', '/partner/'];
var AppSkeleton = function () {
    var loggedIn = hasToken() && !window.location.href.includes('/signup');
    var hasSidebar = sidebarRoutes.some(function (route) { var _a, _b; return (_b = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.pathname) === null || _b === void 0 ? void 0 : _b.includes(route); });
    var sideBarSize = !!localStorage.getItem('sidebar_open');
    return (_jsxs(PageSkeletonWrapper, { children: [isElectron() && (_jsx(AppStatusBarContainer, { children: _jsx(AppStatusBarTitle, { children: "Appspotr - App Builder" }) })), loggedIn && (_jsxs(PageSkeletonBox, { children: [_jsx(PageSkeletonHeader, {}), _jsx(PageSkeletonSubHeader, {})] })), hasSidebar && _jsx(PageSkeletonAppSidebar, { sideBarSize: sideBarSize })] }));
};
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
var skeletonRoot = createRoot(MOUNT_NODE_SKELETON);
skeletonRoot.render(_jsx(AppSkeleton, {}));
var MOUNT_NODE = document.getElementById('app');
var resetAppState = function () { return Promise.all([storageService.removeItem('token')]); };
var App = function (_a) {
    var messages = _a.messages, onAppChanged = _a.onAppChanged;
    var store = useState(configureStore(initialState, history))[0];
    var resetState = useCallback(function () {
        return Promise.all([resetAppState()]).then(function () {
            window.location = '/';
        });
    }, []);
    var themeToken = getTokens();
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyle, {}), _jsx(TailwindStyle, {}), _jsx(Provider, __assign({ store: store }, { children: _jsx(LanguageProvider, __assign({ messages: messages }, { children: _jsx(ThemeProvider, __assign({ theme: { orbit: themeToken } }, { children: _jsx(ConnectedRouter, __assign({ history: history }, { children: _jsx(ErrorReloaderBoundary, __assign({ onReload: resetState }, { children: _jsx(Root, { onAppChanged: onAppChanged }) })) })) })) })) }))] }));
};
var APT_PLATFORM_VERSION_LATEST_KEY = 'APT_PLATFORM_VERSION_LATEST';
var onAppChanged = function (platformVersion, callback) {
    if (callback === void 0) { callback = function () { return null; }; }
    if (process.env.NODE_ENV !== 'production') {
        // dev mode
        callback();
        return;
    }
    var localPlatformVersion = process.env.APT_VERSION || '';
    if (localPlatformVersion === '') {
        // there is no APT_VERSION env variable, something is wrong
        // attempt to continue running normally
        callback();
        return;
    }
    if (!platformVersion) {
        // the user navigated away from an app related page
        // figure out if the page should be reloaded
        var latestPlatformVersion = localStorage.getItem(APT_PLATFORM_VERSION_LATEST_KEY);
        if (!latestPlatformVersion || latestPlatformVersion === '') {
            // there is no latest version number, something went wrong
            // attempt to continue running normally
            callback();
            return;
        }
        if (!isStringMajorMinorEqual(localPlatformVersion, latestPlatformVersion)) {
            // the platform version is different from the latest one
            window.location.reload();
            return;
        }
        // platform version is the same, continue as usual
        callback();
        return;
    }
    // the user navigated to an app related page
    if (!isStringMajorMinorEqual(platformVersion, localPlatformVersion)) {
        // the app platform version is different from the current running one, reload
        // the platform version is different from the latest one
        var href = window.location.href;
        var urlArray = href.split('/');
        if (href.includes('/organizations') && urlArray.length > 6) {
            urlArray.splice(3, 2);
            window.location.replace(urlArray.join('/'));
        }
        else {
            window.location.reload();
        }
        return;
    }
    // platform version is the same, continue as usual
    callback();
};
var render = function (messages) { return __awaiter(void 0, void 0, void 0, function () {
    var root, latestPlatformVersion, registrations, _i, registrations_1, registration;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                root = createRoot(MOUNT_NODE);
                if (process.env.NODE_ENV !== 'production' || !canReceiveServiceWorker()) {
                    if (MOUNT_NODE_SKELETON) {
                        skeletonRoot.unmount();
                    }
                    root.render(_jsx(App, { messages: messages, onAppChanged: onAppChanged }));
                    return [2 /*return*/];
                }
                return [4 /*yield*/, getLatestPlatformVersion()];
            case 1:
                latestPlatformVersion = _a.sent();
                localStorage.setItem(APT_PLATFORM_VERSION_LATEST_KEY, latestPlatformVersion);
                if (!isPresentServiceWorker()) return [3 /*break*/, 6];
                return [4 /*yield*/, getRegistrationsServiceWorker()];
            case 2:
                registrations = (_a.sent()) || [];
                _i = 0, registrations_1 = registrations;
                _a.label = 3;
            case 3:
                if (!(_i < registrations_1.length)) return [3 /*break*/, 6];
                registration = registrations_1[_i];
                console.info('cleaning up service worker registration...');
                return [4 /*yield*/, registration.unregister()];
            case 4:
                _a.sent();
                _a.label = 5;
            case 5:
                _i++;
                return [3 /*break*/, 3];
            case 6:
                if (MOUNT_NODE_SKELETON) {
                    skeletonRoot.unmount();
                }
                root.render(_jsx(App, { messages: messages, onAppChanged: onAppChanged }));
                return [2 /*return*/];
        }
    });
}); };
// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
    new Promise(function (resolve) {
        resolve(import('intl'));
    })
        .then(function () { return Promise.all([import('intl/locale-data/jsonp/en.js')]); })
        .then(function () { return render(translationMessages); })
        .catch(function (err) {
        throw err;
    });
}
else {
    render(translationMessages);
}
